import { ACTION_UPDATE_AB_TESTS } from '@redux/reducers-abtests-factory';
import {
  getCookieName,
  getVariantCookieValue,
} from '@redux/server.get-ab-tests.get-test-value-factory';
import type {
  NextPageReqExtended,
  NextPageResExtended,
} from 'containers/AppContainer/AppContainer.interfaces';
import { addAppExperiment } from 'lib/sdks/analytics/context';
import type { APIFunnelJourney } from 'lib/sdks/strapi';
import { getCookie, setCookie } from 'lib/utils/cookie';

const DAYS_UNTIL_COOKIE_EXPIRES = 30;
const SECONDS_IN_DAY = 86400;

type GetJourneyVariantArgs = {
  journey: APIFunnelJourney;
  storeDispatch: (action: any) => void;
};

type GetJourneyVariantServerArgs = {
  req: NextPageReqExtended;
  res: NextPageResExtended;
} & GetJourneyVariantArgs;

type GetJourneyVariantClientArgs = {
  req?: never;
  res?: never;
} & GetJourneyVariantArgs;

const addABTest = (
  experimentName: string,
  experimentId: string,
  variantId: string | number,
) => {
  addAppExperiment({
    name: experimentName,
    id: experimentId,
    variant: variantId,
  });

  return {
    type: ACTION_UPDATE_AB_TESTS,
    payload: {
      [experimentName]: {
        experimentId,
        variantId,
      },
    },
  };
};

const getVariantServerSide = (req: NextPageReqExtended, cookieName: string) => {
  return req.cookies[cookieName] || null;
};

const getVariantClientSide = (cookieName: string) => {
  return getCookie(cookieName);
};

export const getJourneyVariant = ({
  journey,
  storeDispatch,
  req,
  res,
}: GetJourneyVariantServerArgs | GetJourneyVariantClientArgs) => {
  const isServer =
    typeof document === 'undefined' &&
    typeof req !== 'undefined' &&
    typeof res !== 'undefined';

  if (journey.flows?.length === 0) {
    return journey;
  }

  if (!(journey.experimentId && typeof journey.rollout !== 'undefined')) {
    return journey;
  }

  let variantId: string | null = null;

  const experimentName = `journey-${journey.name
    .toLocaleLowerCase()
    .trim()
    .replace(/ /g, '-')}`;

  const cookieName = getCookieName({
    name: experimentName,
    experimentId: journey.experimentId,
  });

  if (req) {
    variantId = getVariantServerSide(req, cookieName);
  } else if (typeof document !== 'undefined') {
    variantId = getVariantClientSide(cookieName);
  }

  if (variantId === null) {
    variantId = getVariantCookieValue(
      journey.rollout / 100,
      journey.flows.map(flow => ({
        value: flow.name,
        distribution: flow.distribution / 100,
      })),
    ).toString() as string;
  }

  if (isServer) {
    res.cookie(cookieName, variantId, {
      httpOnly: false,
      maxAge: SECONDS_IN_DAY * DAYS_UNTIL_COOKIE_EXPIRES * 1000, // a number representing the milliseconds from Date.now() for expiry
      path: '/',
      secure: true,
    });
  } else {
    setCookie(cookieName, variantId, {
      maxAgeSeconds: SECONDS_IN_DAY * DAYS_UNTIL_COOKIE_EXPIRES,
    });
  }

  storeDispatch(addABTest(experimentName, journey.experimentId, variantId));

  const variantFlow = journey.flows.find(flow => {
    if (flow.name === variantId) {
      return true;
    }

    if (flow.control && variantId === '-1') {
      return true;
    }

    return false;
  });

  return {
    ...journey,
    variantId,
    startingScreen: variantFlow?.startingScreen || undefined,
    paths: variantFlow?.paths || [],
  };
};
